import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";
import { publicAssetUrl } from "../../lib/adminApi";

const statusOptions = ["published", "draft", "archived"];

export default function AdminJournalArticleList() {
  const [articles, setArticles] = useState([]);
  const [issues, setIssues] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [issueId, setIssueId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  async function loadIssues() {
    try {
      const data = await api("/api/admin/journal-issues");
      setIssues(Array.isArray(data) ? data : []);
    } catch {
      setIssues([]);
    }
  }

  async function load() {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    if (issueId) params.set("issue_id", issueId);
    try {
      const data = await api(`/api/admin/journal-articles${params.toString() ? `?${params}` : ""}`);
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadIssues(); }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, issueId]);

  const stats = useMemo(() => articles.reduce((acc, item) => {
    acc.total += 1;
    acc[item.status] = (acc[item.status] || 0) + 1;
    acc.downloads += Number(item.download_count || 0);
    return acc;
  }, { total: 0, published: 0, draft: 0, archived: 0, downloads: 0 }), [articles]);

  async function updateStatus(id, nextStatus) {
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/journal-articles/${id}/status`, { method: "PATCH", body: JSON.stringify({ status: nextStatus }) });
      setMessage("Article status updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteArticle(id) {
    if (!window.confirm("Delete this journal article permanently?")) return;
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/journal-articles/${id}`, { method: "DELETE" });
      setMessage("Article deleted.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <AdminLayout
      title="Journal Articles CMS"
      subtitle="Manage research article metadata, abstracts, authors, keywords, PDFs and download counts."
      actions={<Link to="/adxm/journal-articles/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white">Add Article</Link>}
    >
      <div className="grid gap-4 md:grid-cols-5">
        {[["Total", stats.total], ["Published", stats.published], ["Draft", stats.draft], ["Archived", stats.archived], ["Downloads", stats.downloads]].map(([label, value]) => (
          <div key={label} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <p className="text-sm font-bold text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-black text-slate-950">{value || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_220px_120px]">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title, author, abstract, keyword, DOI..." className="rounded-xl border border-slate-300 px-4 py-3" />
          <select value={issueId} onChange={(e) => setIssueId(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-3">
            <option value="">All issues</option>
            {issues.map((issue) => <option key={issue.id} value={issue.id}>{issue.title}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-3">
            <option value="">All status</option>
            {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <button onClick={load} className="rounded-xl bg-slate-950 px-4 py-3 font-bold text-white">Search</button>
        </div>
      </div>

      {message && <div className="mt-6 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

      <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="p-4">Article</th>
                <th className="p-4">Issue</th>
                <th className="p-4">PDF</th>
                <th className="p-4">Status</th>
                <th className="p-4">Downloads</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="p-6" colSpan="6">Loading articles...</td></tr>
              ) : articles.length === 0 ? (
                <tr><td className="p-6 text-center text-slate-500" colSpan="6">No journal articles found.</td></tr>
              ) : articles.map((article) => (
                <tr key={article.id} className="border-t border-slate-100 align-top">
                  <td className="p-4">
                    <p className="font-black text-slate-950">{article.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{article.authors}</p>
                    <p className="mt-2 line-clamp-2 text-xs text-slate-500">{article.abstract}</p>
                    {Number(article.is_featured) === 1 && <span className="mt-2 inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-bold text-yellow-800">Featured</span>}
                  </td>
                  <td className="p-4">{article.issue_title || "-"}<br /><span className="text-xs text-slate-500">{article.volume || ""} {article.issue || ""}</span></td>
                  <td className="p-4">{article.pdf_url ? <a href={publicAssetUrl(article.pdf_url)} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700">Open PDF</a> : "No PDF"}</td>
                  <td className="p-4">
                    <select value={article.status} onChange={(e) => updateStatus(article.id, e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2">
                      {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </td>
                  <td className="p-4 font-bold">{article.download_count || 0}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/journals/article/${article.slug}`} className="rounded-lg bg-slate-100 px-3 py-2 font-bold text-slate-700">View</Link>
                      <Link to={`/adxm/journal-articles/edit/${article.id}`} className="rounded-lg bg-blue-50 px-3 py-2 font-bold text-blue-700">Edit</Link>
                      <button onClick={() => deleteArticle(article.id)} className="rounded-lg bg-red-50 px-3 py-2 font-bold text-red-700">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
